import React from "react";
import { cn } from "../../lib/utils.js";
import { useRovingTabIndex } from "../../primitives/index.js";

/* ---------------- SegmentedControl ----------------
   Generic over the option value type <T>. React.forwardRef cannot preserve a
   generic type parameter without a lossy cast, so this curated control keeps
   its typed function signature and omits forwardRef — a documented carve-out
   from §14.5 (same rationale as Divider's polymorphic root). */

interface SegmentedOptionBase<T extends string> {
  value: T;
  disabled?: boolean;
  /**
   * Why the option cannot be chosen. Its presence disables the option and
   * shows the reason as visible muted text under the track, linked by the
   * tab's `aria-describedby`. It is never a native `title`.
   */
  disabledReason?: string;
}

interface SegmentedTextOption<T extends string> extends SegmentedOptionBase<T> {
  label: React.ReactNode;
  icon?: React.ReactNode;
  iconOnly?: false;
}

/** A square segment that shows only its icon. `label` is the tab's
 *  accessible name, and its native `title` while the option is enabled. */
interface SegmentedIconOnlyOption<
  T extends string,
> extends SegmentedOptionBase<T> {
  label: string;
  icon: Exclude<React.ReactNode, null | undefined | boolean>;
  iconOnly: true;
}

export type SegmentedOption<T extends string = string> =
  | SegmentedTextOption<T>
  | SegmentedIconOnlyOption<T>;

/** A disabled option: `disabled: true` or a `disabledReason`. */
const isDisabled = (option: SegmentedOption): boolean =>
  Boolean(option.disabled) || option.disabledReason !== undefined;

/** The visible reason. */
const reasonOf = (option: SegmentedOption): string | undefined =>
  option.disabledReason;

/**
 * `role` is omitted so a caller cannot clobber `tablist` — the child buttons are
 * `role="tab"`, and an orphaned tab is an accessibility violation, so the role is
 * this component's contract rather than a default. `onChange` and `defaultValue`
 * are omitted because the DOM's versions collide with the typed ones below. Same
 * shape as `Switch`, which omits its own conflicting attributes for this reason.
 *
 * Extending `HTMLAttributes` is what makes the tablist nameable: `aria-label` and
 * `aria-labelledby` arrive through `AriaAttributes` and reach the div via `rest`.
 * Before this, a caller passing `aria-label` had it silently dropped, and any page
 * with two segmented controls — or one beside a `Tabs` — offered a screen-reader
 * user several identical anonymous tablists.
 */
export interface SegmentedControlProps<T extends string = string> extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onChange" | "defaultValue" | "role"
> {
  options: SegmentedOption<T>[];
  value?: T;
  defaultValue?: T;
  onChange?: (value: T) => void;
  size?: "sm" | "md";
  /**
   * Active-segment fill. Omitted, the selected segment takes the teal
   * selection paint (the default state, not a tone). `ai` is the iris fill
   * the design canvas uses for Player AI controls (ADR-0076); `neutral` is
   * the quiet raised segment the VSM workshop artboards draw — the active
   * option lifts to the card surface on a sunken track, with no colour at
   * all.
   */
  tone?: "neutral" | "ai";
}

export function SegmentedControl<T extends string = string>({
  options,
  value,
  defaultValue,
  onChange,
  size = "md",
  tone,
  className,
  ...rest
}: SegmentedControlProps<T>): JSX.Element {
  const isControlled = value !== undefined;
  const [internal, setInternal] = React.useState<T | undefined>(
    defaultValue ?? options[0]?.value,
  );
  // No unsafe cast: with an empty options array `internal` is undefined and
  // simply no segment renders active.
  const current: T | undefined = isControlled ? value : internal;

  const select = React.useCallback(
    (next: T) => {
      if (!isControlled) setInternal(next);
      onChange?.(next);
    },
    [isControlled, onChange],
  );

  /* THE TABLIST KEYBOARD CONTRACT (APG, automatic activation).
     `role="tablist"` was asserted from the first version of this control while
     none of the behaviour it promises existed: Tab reached every segment in
     turn and the arrow keys did nothing, so a screen-reader user was told this
     was a tab list and then found it was not one. Mirrors `Tabs` — the same
     `useRovingTabIndex` primitive, the same `navigatingRef` guard so an
     external selection change re-anchors WITHOUT stealing focus, and the same
     automatic activation, which is also what makes the keyboard agree with the
     mouse here: a click selects immediately, so an arrow must too.

     The one difference from `Tabs` is that navigation runs over the ENABLED
     options only. A disabled `<button>` cannot take focus, so stepping onto one
     would drop focus to the body and strand the user mid-list — and unlike
     `Tabs`, a disabled segment with a `disabledReason` is an ordinary case here. */
  const enabledIndexes = React.useMemo(
    () =>
      options.reduce<number[]>((acc, option, index) => {
        if (!isDisabled(option)) acc.push(index);
        return acc;
      }, []),
    [options],
  );
  const selectedIndex = options.findIndex((o) => o.value === current);
  const selectedEnabledPos = Math.max(0, enabledIndexes.indexOf(selectedIndex));
  const buttonRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const navigatingRef = React.useRef(false);

  const onActiveChange = React.useCallback(
    (pos: number) => {
      if (!navigatingRef.current) return;
      const index = enabledIndexes[pos];
      if (index === undefined) return;
      const option = options[index];
      if (!option) return;
      buttonRefs.current[index]?.focus();
      select(option.value);
    },
    [enabledIndexes, options, select],
  );

  const {
    activeIndex: activeEnabledPos,
    setActiveIndex,
    onKeyDown: rovingKeyDown,
  } = useRovingTabIndex({
    itemCount: enabledIndexes.length,
    orientation: "horizontal",
    loop: true,
    initialIndex: selectedEnabledPos,
    onActiveChange,
  });

  React.useEffect(() => {
    setActiveIndex(selectedEnabledPos);
  }, [selectedEnabledPos, setActiveIndex]);

  /* Which segment Tab reaches. One tab stop for the whole control is the other
     half of the tablist contract — without it the arrow keys would be an
     addition rather than a replacement, and a four-option filter would still
     cost four Tab presses to walk past. */
  const tabbableIndex = enabledIndexes[activeEnabledPos] ?? selectedIndex;

  /* THE SLIDING INDICATOR (ADR-0091). `Tabs` has slid its underline since
     ADR-0092 while this control's active fill simply appeared in its new
     place, so a segmented control beside a tab row read as two design systems
     on one page. This is ADDITIVE: the active segment keeps its own fill, and
     because that fill cross-fades on `transition-colors` the underlay
     travelling beneath it is what the eye follows between the two positions.

     Measured, not derived from the index: segments are as wide as their
     labels. `offsetTop`/`offsetHeight` come along because this track WRAPS
     (see the container below) — an indicator pinned to one row would sit on
     the wrong line the moment it does. Hidden until the first measurement
     lands — jsdom and an unlaid-out track both report zero — so it never
     flashes at the start of the row. Same shape as `Tabs`. */
  const trackRef = React.useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = React.useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  React.useLayoutEffect(() => {
    const measure = (): void => {
      const segment =
        selectedIndex >= 0 ? buttonRefs.current[selectedIndex] : null;
      /* A selected segment that is DISABLED paints no fill today, and the
         indicator must not invent one for it: a teal block under muted text
         reads worse than the missing selection mark it would stand in for. */
      const selected = options[selectedIndex];
      if (
        !segment ||
        segment.offsetWidth === 0 ||
        (selected && isDisabled(selected))
      ) {
        setIndicator((prev) => (prev === null ? prev : null));
        return;
      }
      const {
        offsetLeft: left,
        offsetTop: top,
        offsetWidth: width,
        offsetHeight: height,
      } = segment;
      setIndicator((prev) =>
        prev &&
        prev.left === left &&
        prev.top === top &&
        prev.width === width &&
        prev.height === height
          ? prev
          : { left, top, width, height },
      );
    };
    measure();
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    return () => observer.disconnect();
  }, [selectedIndex, options]);
  // Direction B canvas: segments are 30px tall at 13px (`md`) and 26px at 12px
  // (`sm`), 15px / 11px of side padding, radius 6px inside an 8px wrapper.
  const sz =
    size === "sm"
      ? "h-6.5 text-caption px-2.75"
      : "h-7.5 text-body-small px-3.75";
  // An icon-only segment is square at the same height (26px / 30px), so the
  // indicator, measured from the segment, is square too.
  const iconOnlySz =
    size === "sm"
      ? "h-6.5 w-6.5 shrink-0 justify-center text-caption px-0"
      : "h-7.5 w-7.5 shrink-0 justify-center text-body-small px-0";
  const ai = tone === "ai";
  const activeTone = ai
    ? "bg-pb-iris-interactive border-pb-iris-interactive text-pb-primary-foreground"
    : tone === "neutral"
      ? "bg-pb-card border-pb-border text-pb-text shadow-sm"
      : "bg-pb-teal border-pb-teal text-pb-shell";
  // The indicator paints the fill the segment lands in — the same colour, so
  // the travel is invisible as a colour change and readable as a movement.
  const indicatorTone = ai
    ? "bg-pb-iris-interactive"
    : tone === "neutral"
      ? "bg-pb-card"
      : "bg-pb-teal";
  /* A `disabledReason` is visible text under the track, outside the tablist
     (whose children must all be tabs). Only then does a wrapper exist, and it
     takes `className`, so a control with no reason renders today's DOM. */
  const reasonId = React.useId();
  const hasReasons = options.some((o) => reasonOf(o) !== undefined);
  const track = (
    <div
      // Spread first, unlike Badge and Divider, so `role` below cannot be
      // clobbered at runtime. Omitting `role` from the props type stops it in
      // TypeScript, but a caller reaching this via their own spread — or simply
      // ignoring the error — would otherwise leave `role="tab"` children
      // orphaned, which is an accessibility violation rather than a style choice.
      {...rest}
      ref={trackRef}
      role="tablist"
      className={cn(
        // `flex-wrap` because the control was previously a single unwrapping
        // row: past the available width it simply overflowed and was clipped
        // by whatever contained it, silently putting options out of reach. The
        // responsive-floor guardrail did not catch it either, since the clip
        // happened inside the parent rather than overflowing the document.
        //
        // `max-w-full` is what lets that wrapping actually happen. An
        // `inline-flex` box is sized by its content, so inside a flex row a
        // caller's `shrink-0` — or simply a row with no spare width — leaves it
        // wider than its container and the last segment is clipped again, with
        // `flex-wrap` never reached. Capping the box at the container's width
        // is the fix at the level the bug lives at, so no call site has to know
        // to avoid `shrink-0`. Nothing moves while there IS room.
        //
        // `gap-y-0.5` is deliberately vertical-only: it separates wrapped rows
        // without shifting a single-row control by a pixel, so no existing
        // consumer's layout or VR baseline moves.
        // `relative` makes this box the indicator's offset parent, so the
        // measurements below and the span's position share one origin.
        "relative inline-flex max-w-full flex-wrap gap-0.5 p-0.5 rounded-md border border-pb-border",
        // The neutral tone's track sits BELOW the card surface its active
        // segment lifts to; the fills keep the elevated track they always had.
        tone === "neutral" ? "bg-pb-page/60" : "bg-pb-card-elevated",
        !hasReasons && className,
      )}
    >
      <span
        aria-hidden
        data-segment-indicator
        className={cn(
          "pointer-events-none absolute rounded-sm",
          "pb-slider-indicator motion-reduce:transition-none",
          indicatorTone,
          indicator ? "opacity-100" : "opacity-0",
        )}
        /* Anchored at the track's own top-left and moved by `transform`, so
           the physical `offsetLeft`/`offsetTop` pair needs no flipping and the
           same maths holds in RTL. The inline `left`/`top` are the anchor, not
           the position — the logical-property lint bans the class form for
           exactly the case this is not. */
        style={{
          left: 0,
          top: 0,
          transform: `translate(${indicator?.left ?? 0}px, ${indicator?.top ?? 0}px)`,
          width: indicator?.width ?? 0,
          height: indicator?.height ?? 0,
        }}
      />
      {options.map((o, index) => {
        const active = current === o.value;
        const disabled = isDisabled(o);
        const hasReason = reasonOf(o) !== undefined;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            ref={(node) => {
              buttonRefs.current[index] = node;
            }}
            aria-selected={active}
            aria-disabled={disabled || undefined}
            aria-label={o.iconOnly ? o.label : undefined}
            aria-describedby={hasReason ? `${reasonId}-${index}` : undefined}
            disabled={disabled}
            tabIndex={index === tabbableIndex ? 0 : -1}
            /* An icon-only label while the option can be pressed. Never a
               reason, which is visible text below. */
            title={!disabled && o.iconOnly ? o.label : undefined}
            onClick={() => {
              if (disabled) return;
              select(o.value);
            }}
            /* On the TAB, not the tablist — the focused element is what
               receives the key, the tablist itself is not focusable (and
               `jsx-a11y/interactive-supports-focus` correctly refuses a
               keyboard handler on a non-focusable interactive role). A key this
               does not own still bubbles to the container, so a caller's own
               `onKeyDown` keeps working. Same shape as `Tabs`. */
            onKeyDown={(event) => {
              navigatingRef.current = true;
              rovingKeyDown(event);
              navigatingRef.current = false;
            }}
            className={cn(
              /* `relative` is what keeps the label above the indicator: the
                 indicator is absolutely positioned, and a positioned element
                 paints over in-flow siblings whatever the source order. Both
                 are then positioned with no z-index, so tree order decides and
                 the segments — written after the span — win. No z-index class
                 (the raw scale is lint-banned) and no visual change. */
              "relative inline-flex items-center gap-1.5 rounded-sm transition-colors font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal/50",
              o.iconOnly ? iconOnlySz : sz,
              disabled
                ? "text-pb-text-muted/60 border border-transparent cursor-not-allowed"
                : active
                  ? `${activeTone} border font-semibold`
                  : "text-pb-text-muted hover:text-pb-text hover:pb-overlay-paint active:bg-pb-card-elevated border border-transparent",
            )}
          >
            {o.icon}
            {o.iconOnly ? null : o.label}
          </button>
        );
      })}
    </div>
  );
  if (!hasReasons) return track;
  return (
    <div
      className={cn(
        "inline-flex max-w-full flex-col items-start gap-1",
        className,
      )}
    >
      {track}
      {options.map((o, index) => {
        const reason = reasonOf(o);
        return reason === undefined ? null : (
          <p
            key={o.value}
            id={`${reasonId}-${index}`}
            className="text-dense-1 text-pb-text-muted"
          >
            {reason}
          </p>
        );
      })}
    </div>
  );
}
